// Field schemas driving the generic OARecordModule for all OA-linked modules.
// type: text | number | date | textarea | select | currency ; list fields use "items".
// Master linkage (Search → Select → Add New → auto-fill):
//   master:   master-type key (see config/masterDefs.js) OR "product" / "customer"
//   valueField: which property of the selected master record is stored in this field (default "name")
//   autofill: { targetFieldKey: masterRecordProperty } filled on selection
//   setsCustomer: true → selecting also sets the record's top-level customerName
export const REC_SCHEMAS = {
  office_work: {
    title: "OA / Office Work",
    columns: ["OA No.", "Customer", "Destination", "Items"],
    fields: [
      { key: "oaDate", label: "OA Date", type: "date" },
      { key: "consignee", label: "Consignee", master: "customer", setsCustomer: true,
        autofill: { consigneeAddress: "consigneeAddress", gstNumber: "gstNumber" } },
      { key: "destination", label: "Destination", master: "destination" },
    ],
    itemsKey: "items",
    itemFields: [
      { key: "item", label: "Item", master: "product" },
      { key: "qty", label: "Qty", type: "number" },
    ],
  },
  invoice: {
    title: "Invoice",
    columns: ["OA No.", "Customer", "Invoice No.", "Invoice Date", "Total"],
    fields: [
      { key: "srNo", label: "Sr. No." },
      { key: "invoiceNo", label: "Invoice No.", required: true },
      { key: "invoiceDate", label: "Invoice Date", type: "date" },
      { key: "gstPercent", label: "GST %", type: "number" },
    ],
    itemsKey: "items",
    itemFields: [
      { key: "description", label: "Item Description", master: "product",
        autofill: { itemCode: "productCode", price: "defaultUnitRate" } },
      { key: "itemCode", label: "Item Code", master: "product", valueField: "productCode",
        autofill: { description: "itemName", price: "defaultUnitRate" } },
      { key: "qty", label: "Qty", type: "number" },
      { key: "price", label: "Price", type: "number" },
    ],
    totals: ["subtotal", "gstAmount", "totalAmount"],
    print: true,
  },
  item: {
    title: "Item / Product Tracking",
    addLabel: "Add Product",
    columns: ["OA No.", "Customer", "Item", "Serial No.", "Body No.", "Packing"],
    fields: [
      { key: "item", label: "Item", master: "product", valueField: "itemName",
        autofill: { description: "itemName" } },
      { key: "description", label: "Item Description" },
      { key: "specification", label: "Item Specification", master: "specification" },
      { key: "bodyNo", label: "Body No.", master: "body_no" },
      { key: "serialNo", label: "Serial No.", master: "serial_no" },
      { key: "keyNo", label: "Key No.", master: "key_no" },
      { key: "keyEmbossing", label: "Key Embossing", master: "key_embossing" },
      { key: "mKeyNo", label: "M. Key No.", master: "m_key_no" },
      { key: "extraLocks", label: "Extra Locks", master: "extra_lock" },
      { key: "extraParts", label: "Extra Parts", master: "extra_part" },
      { key: "specialInfo", label: "Special Information", master: "special_info", full: true },
      { key: "packing", label: "Packing", master: "packing" },
    ],
  },
  dispatch: {
    title: "Dispatch",
    columns: ["OA No.", "Customer", "AURO-1 GP", "AURO-2 GP", "Vehicle No."],
    fields: [
      { key: "auro1GatePass", label: "AURO-1 Gate Pass No.", master: "gate_pass" },
      { key: "auro2GatePass", label: "AURO-2 Gate Pass No.", master: "gate_pass" },
      { key: "localVehicleNo", label: "Local Vehicle No.", master: "vehicle" },
      { key: "localRent", label: "Local Rent", type: "number" },
      { key: "qty", label: "Qty", type: "number" },
      { key: "weightPerPiece", label: "Weight / Piece (kg)", type: "number" },
    ],
  },
  transporter: {
    title: "Transporter",
    columns: ["OA No.", "Customer", "Transport Name", "LR No.", "Vehicle No."],
    masterField: { key: "transportName", label: "Transport Name", master: "transporter",
      autofill: { transporterContact: "contact", transportAddress: "address", transportLocation: "location" } },
    fields: [
      { key: "transporterContact", label: "Transporter Contact No." },
      { key: "transportAddress", label: "Transport Address" },
      { key: "transportLocation", label: "Transport Location" },
      { key: "localVehicleNo", label: "Local Vehicle No.", master: "vehicle" },
      { key: "localFare", label: "Local Fare", type: "number" },
      { key: "craneCharge", label: "Loading/Unloading Crane Charge", type: "number" },
      { key: "destinationFare", label: "Destination Fare", type: "number" },
      { key: "lrNo", label: "LR No.", master: "lr" },
      { key: "vehicleNo", label: "Vehicle No.", master: "vehicle" },
      { key: "approxDeliveryDate", label: "Approx. Delivery Date", type: "date" },
      { key: "godownName", label: "Destination Godown", master: "godown",
        autofill: { godownAddress: "address", godownContactNo: "contactNo", godownContactPerson: "contactPerson" } },
      { key: "godownAddress", label: "Destination Godown Address" },
      { key: "godownContactNo", label: "Godown Contact No." },
      { key: "godownContactPerson", label: "Godown Contact Person" },
      { key: "hamaliCharge", label: "Loading/Unloading Hamali Charge", type: "number" },
      { key: "demurrageCharges", label: "Demurrage Charges", type: "number" },
      { key: "packetNo", label: "Packet No.", master: "packet_no" },
    ],
    files: [
      { field: "handover_letter", label: "Material Handover Letter" },
      { field: "unloaded_photo", label: "Unloaded Item Photo" },
    ],
  },
  installer: {
    title: "Installer",
    columns: ["OA No.", "Customer", "Team", "Status"],
    hasStatus: true,
    statusOptions: ["pending", "in_progress", "completed", "hold"],
    masterField: { key: "teamName", label: "Installation Team Name", master: "installer",
      autofill: { gstServiceTax: "gst" } },
    fields: [
      { key: "gstServiceTax", label: "GST No. / Service Tax No." },
      { key: "pickupPerson", label: "Material Pickup Person (Name)", master: "pickup_person" },
      { key: "pickupAadhaarNo", label: "Pickup Person Aadhaar Number" },
      { key: "pickupPanNo", label: "Pickup Person PAN Number" },
      { key: "godownDistance", label: "Godown to Destination Distance (km)", type: "number" },
      { key: "vehicleNo", label: "Vehicle No.", master: "vehicle" },
      { key: "ewayBillNo", label: "E-way Bill No.", master: "eway_bill" },
      { key: "localFare", label: "Local Fare", type: "number" },
      { key: "installationCharge", label: "Installation Charge", type: "number" },
      { key: "weldingCharge", label: "Welding Charge", type: "number" },
      { key: "craneCharge", label: "Crane Charge", type: "number" },
      { key: "unionCharges", label: "Union Charges", type: "number" },
    ],
    files: [
      { field: "eway_bill", label: "E-way Bill" },
      { field: "aadhaar", label: "Aadhaar Card" },
      { field: "pan", label: "PAN Card" },
      { field: "unloaded_photo", label: "Unloaded Item Photo" },
    ],
  },
};

export const REC_ROUTES = {
  office_work: "office-work", invoice: "invoice-data", item: "item-tracking",
  dispatch: "dispatch", transporter: "transporter", installer: "installer",
};
